import React, { useEffect, useState } from 'react';
import { Card, Col, Divider, Grid, Row, Table, Typography } from 'antd';
import MainLogo from '@app/assets/images/logo.jpg';
import SecondLogo from '@app/assets/images/logo_crop.png';
import { getReceiptById } from '@app/api/finances.api';

interface ReceiptProps {
  id: any;
  setCurrentBreadcrumName: React.Dispatch<React.SetStateAction<string>>;
}

const { useBreakpoint } = Grid;

const Receipt: React.FC<ReceiptProps> = ({ id, setCurrentBreadcrumName }) => {
  const screens = useBreakpoint();
  const isMobile = screens.xs;

  const [receipt, setReceipt] = useState<any>(null);
  const [isLab, setIsLab] = useState<boolean>(false);
  const [divisionCodeToName, setDivisionCodeToName] = useState<{ [code: string]: string }>({});

  useEffect(() => {
    const storedIsLab = localStorage.getItem('isLab');
    setIsLab(storedIsLab === 'true');
  }, []);

  useEffect(() => {
    if (id == null) return;

    const getReceiptData = async () => {
      try {
        const data = await getReceiptById(id);
        setReceipt(data.receipt);
        setCurrentBreadcrumName(`Receipt #${data.receipt.receipt_code}`);
      } catch (error) {
        console.error('Failed to fetch receipt:', error);
      }
    };

    getReceiptData();
  }, [id]);

  if (!receipt) return null;

  const receiptItemsGroupedByDivision = () => {
    const grouped: { [divisionCode: string]: any[] } = {};

    receipt.invoice.invoice_lines.forEach((line: any, index: number) => {
      const match = line.description?.match(/^([^/]+)/);
      const divisionCode = match ? match[1] : 'Unknown Division';
      //console.log(`Line ${index} division code:`, divisionCode);
      if (!grouped[divisionCode]) grouped[divisionCode] = [];
      grouped[divisionCode].push({
        key: `${divisionCode}-${index}`,
        itemCode: line.itemable?.code || '-',
        description: line.itemable?.name || '-',
        qty: line.qty || '-',
        price: `LKR ${line.unit_price}`,
        total: `LKR ${line.total_price}`,
      });
    });

    const dataSource: any[] = [];

    Object.entries(grouped).forEach(([divisionCode, lines]) => {
      const friendlyName = divisionCodeToName[divisionCode] || divisionCode;

      dataSource.push({
        key: `header-${divisionCode}`,
        itemCode: friendlyName,
        divisionCode,
        isHeader: true,
      });
      dataSource.push(...lines);
    });

    return dataSource;
  };

  const receiptItems = receipt.invoice.invoice_lines.map((line: any, index: number) => ({
    key: index.toString(),
    itemCode: line.itemable?.code || '-',
    description: line.description || '-',
    qty: line.qty || '-',
    price: `LKR ${line.unit_price}`,
    total: `LKR ${line.total_price}`,
  }));

  const columns = [
    {
      title: 'Item Code',
      dataIndex: 'itemCode',
      key: 'itemCode',
      render: (text: any, record: any) => {
        if (record.isHeader) {
          return {
            children: <strong>{divisionCodeToName[record.divisionCode] || record.divisionCode}</strong>,
            props: { colSpan: 5, style: { background: '#f0f2f5' } },
          };
        }
        if (record.isTotal) return { children: null, props: { colSpan: 1 } };
        return text;
      },
    },
    {
      title: 'Item Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: any, record: any) =>
        record.isHeader || record.isTotal ? { children: null, props: { colSpan: 0 } } : text,
    },
    {
      title: 'QTY',
      dataIndex: 'qty',
      key: 'qty',
      render: (text: any, record: any) =>
        record.isHeader || record.isTotal ? { children: null, props: { colSpan: 0 } } : text,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (text: any, record: any) => {
        if (record.isTotal) return { children: <strong>{text}</strong>, props: {} };
        return record.isHeader ? { children: null, props: { colSpan: 0 } } : text;
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (text: any, record: any) => {
        if (record.isTotal) return { children: <strong>{text}</strong>, props: {} };
        return record.isHeader ? { children: null, props: { colSpan: 0 } } : text;
      },
    },
  ];

  const formatDate = (date: string) => {
    const newDate = new Date(date);
    return newDate.toLocaleString('sv-SE');
  };

  const totalAmount = Number(receipt.amount || 0);
  const paidAmount = Number(receipt.invoice.paid_amount || 0);
  const dueAmount = Number(receipt.invoice.due_amount || 0);

  const formatCurrency = (amount: number) =>
    `LKR ${amount.toLocaleString('en-LK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <div id="receipt-content">
      <Card
        style={{
          borderRadius: '16px',
          padding: '20px',
          textAlign: 'center',
          margin: 'auto',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <img src={MainLogo} alt="Main Logo" style={{ height: isMobile ? '60px' : '80px' }} />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'center' : 'space-between',
            alignItems: 'center',
            marginTop: '30px',
          }}
        >
          <div style={{ flex: isMobile ? 'none' : '1', textAlign: isMobile ? 'center' : 'left' }}>
            <img src={SecondLogo} alt="Second Logo" style={{ height: isMobile ? '50px' : '70px' }} />
          </div>
          <div style={{ flex: '2', textAlign: 'center' }}>
            <h2 style={{ marginTop: '30px', fontSize: isMobile ? '24px' : '40px', fontWeight: 'bold' }}>Receipt</h2>
          </div>

          {/* Invisible Spacer in Desktop Mode for Proper Centering */}
          {!isMobile && <div style={{ flex: '1' }} />}
        </div>

        <Divider style={{ backgroundColor: '#D3D3D3', marginTop: '10px' }} />

        <div
          style={{
            // paddingLeft: isMobile ? 0 : '20px',
            marginTop: isMobile ? '40px' : '60px',
          }}
        >
          {/* ===== Row 1 ===== */}
          <Row gutter={[24, 24]} style={{ marginTop: '30px' }}>
            <Col xs={24} md={8}>
              <Typography.Text strong>Receipt Number:</Typography.Text>
              <div>{receipt?.code}</div>
            </Col>

            <Col xs={24} md={8}>
              <Typography.Text strong>Cashier:</Typography.Text>
              <div>{receipt?.issuer?.name}</div>
            </Col>

            <Col xs={24} md={8}>
              <Typography.Text strong>Cashier Location:</Typography.Text>
              <div style={{ whiteSpace: 'pre-line' }}>
                {receipt?.invoice?.department?.name}
                <br />
                {receipt?.invoice?.department?.address}
              </div>
            </Col>
          </Row>

          {/* ===== Row 2 ===== */}
          <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
            <Col xs={24} md={8}>
              <Typography.Text strong>Receipt Issue Date:</Typography.Text>
              <div>{formatDate(receipt?.issued_at)}</div>
            </Col>

            <Col xs={24} md={8}>
              <Typography.Text strong>Payment Method:</Typography.Text>
              <div>{receipt?.payment_method}</div>
            </Col>

            {!isLab && (
              <Col xs={24} md={8}>
                <Typography.Text strong>Invoice Department:</Typography.Text>
                <div>{receipt?.invoice?.department?.name}</div>
              </Col>
            )}
          </Row>
        </div>

        <Divider style={{ backgroundColor: '#D3D3D3', marginTop: isMobile ? '40px' : '60px' }} />
        <h2 style={{ marginTop: '30px', fontSize: isMobile ? '16px' : '26px', fontWeight: 'bold', color: '#A9A9A9' }}>
          {receipt.invoice.partner_type === 'App\\Models\\Customer' ? 'Customer Details' : 'Department Details'}
        </h2>
        <div
          style={{
            // paddingLeft: isMobile ? 0 : '80px',
            marginTop: isMobile ? '40px' : '60px',
          }}
        >
          <Row gutter={[24, 24]} style={{ marginTop: '30px' }}>
            <Col xs={24} md={6}>
              <Typography.Text strong>Name:</Typography.Text>
              <div>
                {receipt?.invoice?.partner
                  ? `${receipt.invoice.partner.first_name} ${receipt.invoice.partner.last_name}`
                  : 'N/A'}
              </div>
            </Col>

            <Col xs={24} md={6}>
              <Typography.Text strong>Address:</Typography.Text>
              <div style={{ whiteSpace: 'pre-line' }}>{receipt?.invoice?.partner?.address || 'N/A'}</div>
            </Col>

            {receipt?.invoice?.partner_type === 'App\\Models\\Customer' && (
              <>
                <Col xs={24} md={6}>
                  <Typography.Text strong>Email:</Typography.Text>
                  <div>{receipt?.invoice?.partner?.user?.email || 'N/A'}</div>
                </Col>

                <Col xs={24} md={6}>
                  <Typography.Text strong>Contact No:</Typography.Text>
                  <div>{receipt?.invoice?.partner?.contact_no || 'N/A'}</div>
                </Col>
              </>
            )}
          </Row>
        </div>

        <Divider
          style={{
            backgroundColor: '#D3D3D3',
            marginTop: isMobile ? '40px' : '60px',
            marginBottom: isMobile ? '40px' : '60px',
          }}
        />
        <Table
          columns={columns}
          dataSource={isLab ? receiptItemsGroupedByDivision() : receiptItems}
          pagination={false}
          bordered
          style={{ marginTop: '30px' }}
          summary={() => (
            <>
              {/* TOTAL */}
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <div style={{ textAlign: 'right' }}>
                    <strong>Total Amount</strong>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>{formatCurrency(totalAmount)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>

              {/* PAID */}
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <div style={{ textAlign: 'right' }}>Amount Paid</div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>{formatCurrency(paidAmount)}</Table.Summary.Cell>
              </Table.Summary.Row>

              {/* DUE */}
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <div style={{ textAlign: 'right' }}>
                    <strong>Balance Due</strong>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>{formatCurrency(dueAmount)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          )}
          scroll={{ x: true }} // Ensures table scrolls on small screens
        />

        {/* <Divider style={{ backgroundColor: '#D3D3D3', marginTop: isMobile ? '40px' : '60px' }} />
        <div style={{ paddingLeft: isMobile ? 0 : '80px', marginTop: isMobile ? '20px' : '30px' }}>
          <Row
            gutter={[64, 64]}
            style={{
              marginTop: '30px',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            <Col
              xs={24}
              md={24}
              lg={8}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Bank:</Typography.Text>
              <div> People&apos;s Bank</div>
            </Col>
            <Col
              xs={24}
              md={24}
              lg={8}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Account:</Typography.Text>
              <div>0000 0000 000000</div>
            </Col>
            <Col
              xs={24}
              md={24}
              lg={8}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Branch:</Typography.Text>
              <div>Katunayake</div>
            </Col>
          </Row>
        </div> */}
        <p style={{ textAlign: 'left', marginTop: '80px' }}>
          {' '}
          * <span style={{ marginRight: '10px' }}></span> This is a computer generated Receipt
        </p>
      </Card>
    </div>
  );
};

export default Receipt;
